import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompanyHeritage = () => {
  const navigate = useNavigate();

  const heritageStats = [
    {
      id: 1,
      number: "10+",
      label: "Years of Excellence",
      description: "Serving the textile industry with dedication and innovation",
      icon: "Calendar"
    },
    {
      id: 2,
      number: "1500+",
      label: "Satisfied Buyers",
      description: "Building lasting partnerships across Gujarat and beyond",
      icon: "Users"
    },
    {
      id: 3,
      number: "5+",
      label: "Fabric Varieties",
      description: "Premium quality fabrics from manufacturing to wholesale",
      icon: "Package"
    },
    {
      id: 4,
      number: "100%",
      label: "Client Satisfaction",
      description: "Maintaining the highest standards in every project",
      icon: "Award"
    }
  ];

  const coreValues = [
    {
      id: 1,
      title: "Premium Quality",
      description: "Honoring the rich heritage of Indian textile craftsmanship while maintaining modern quality standards.",
      icon: "Award",
      color: "textile"
    },
    {
      id: 2,
      title: "Competitive Pricing",
      description: "Offering wholesale and retail pricing that provides the best value for premium quality fabrics.",
      icon: "TrendingDown",
      color: "secondary"
    },
    {
      id: 3,
      title: "Fabric Excellence",
      description: "Uncompromising commitment to superior fabric quality, finish, and durability in every meter.",
      icon: "Star",
      color: "golden"
    },
    {
      id: 4,
      title: "Customer Partnership",
      description: "Building long-term relationships through trust, reliability, and consistent fabric supply.",
      icon: "Handshake",
      color: "primary"
    }
  ];

  const handleLearnMore = () => {
    navigate('/about');
  };

  return (
    <section className="py-8 sm:py-12 lg:py-16 xl:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heritage Story */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-center mb-8 sm:mb-12 lg:mb-16">
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-poppins font-bold text-primary mb-3 sm:mb-4 lg:mb-6 leading-tight">
                Heritage Meets Innovation
              </h2>
              <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-text-secondary leading-relaxed mb-3 sm:mb-4 lg:mb-6">
                Founded with a vision to deliver premium quality fabrics, Kunjvihari Impex has been a trusted name in fabric manufacturing and wholesale supply for over 10 years. We are experts in manufacturing position print fabrics.
              </p>
              <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-text-secondary leading-relaxed mb-4 sm:mb-6 lg:mb-8">
                Located in Surat, Gujarat - the heart of India's textile industry, we specialize in manufacturing premium fabrics including Position Print, Polyester, Natural Crepe, Velvet Embroidery, Chinon, and Shimmer fabrics that combine traditional craftsmanship with modern quality standards.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
              <Button
                variant="default"
                size="lg"
                className="btn-textile text-sm sm:text-base lg:text-lg font-semibold w-full sm:w-auto"
                onClick={handleLearnMore}
                iconName="BookOpen"
                iconPosition="left"
                iconSize={20}
              >
                Our Complete Story
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-sm sm:text-base lg:text-lg font-semibold w-full sm:w-auto"
                onClick={() => navigate('/portfolio')}
                iconName="Eye"
                iconPosition="left"
                iconSize={20}
              >
                View Our Work
              </Button>
            </div>
          </div>

          <div className="relative order-first lg:order-last">
            <div className="heritage-pattern absolute inset-0 rounded-xl sm:rounded-2xl"></div>
            <video
              src="/assets/portfolio/Hero_section.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-64 sm:h-80 md:h-96 lg:h-[420px] xl:h-[480px] object-cover rounded-xl sm:rounded-2xl shadow-heritage relative z-10"
            />
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-12 lg:mb-16">
          {heritageStats?.map((stat) => (
            <div
              key={stat?.id}
              className="text-center p-3 sm:p-4 lg:p-6 card-heritage group hover:shadow-textile transition-all duration-300"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-heritage rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4 group-hover:scale-110 transition-transform duration-300">
                <Icon name={stat?.icon} size={16} color="white" className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-poppins font-bold text-primary mb-1 lg:mb-2">
                {stat?.number}
              </div>
              <div className="text-xs sm:text-sm lg:text-base xl:text-lg font-semibold text-text-primary mb-1 lg:mb-2">
                {stat?.label}
              </div>
              <p className="text-[10px] sm:text-xs lg:text-sm text-text-secondary leading-snug">
                {stat?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Core Values */}
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
          <h3 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-poppins font-bold text-primary mb-2 sm:mb-3 lg:mb-4">
            Our Core Values
          </h3>
          <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-text-secondary max-w-3xl mx-auto px-4">
            The principles that guide our commitment to excellence and drive our passion for fabric quality and customer satisfaction.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {coreValues?.map((value) => (
            <div
              key={value?.id}
              className="text-center p-4 sm:p-5 lg:p-6 card-elevated group hover:shadow-heritage transition-all duration-300"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-${value?.color} rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <Icon name={value?.icon} size={16} color="white" className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
              <h4 className="text-sm sm:text-base lg:text-lg xl:text-xl font-poppins font-semibold text-primary mb-1.5 sm:mb-2 lg:mb-3">
                {value?.title}
              </h4>
              <p className="text-xs sm:text-sm lg:text-base text-text-secondary leading-relaxed">
                {value?.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompanyHeritage;